import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { of, timer } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { UserApiService } from '../api/user/user-api.service';
import { Store } from '@ngrx/store';
import { SELECT_IS_USER_EMAIL_VERIFICATION_PLUGIN_ACTIVE } from '@store/authentication/user-email-verification.selection';
@Injectable({ providedIn: 'root' })
export class AuthVerificationEmailService {
  private readonly userApiService = inject(UserApiService);
  private readonly store = inject(Store);

  private readonly LAST_SENT_KEY = 'last_verification_email_sent';
  private readonly FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

  public readonly isUserEmailVerificationPluginActive = this.store.selectSignal(
    SELECT_IS_USER_EMAIL_VERIFICATION_PLUGIN_ACTIVE,
  );

  sendVerificationEmail$() {
    const lastSentTime = localStorage.getItem(this.LAST_SENT_KEY);
    const now = new Date();

    // Check if more than 5 minutes have passed since the last email was sent
    if (lastSentTime) {
      const lastSent = new Date(lastSentTime);
      const timeDifference = now.getTime() - lastSent.getTime();

      if (timeDifference < this.FIVE_MINUTES_IN_MS) {
        // Less than 5 minutes have passed, do not send the email
        return of(null);
      }
    }

    // Store the current date in localStorage
    localStorage.setItem(this.LAST_SENT_KEY, now.toISOString());

    // Call the private function to send the email
    return this._sendVerificationEmail();
  }

  private _sendVerificationEmail() {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('email-verified', 'true');
    const redirectTo = currentUrl.toString();
    return this.userApiService.sendUserEmailVerification$(redirectTo).pipe(
      take(1),
      tap({
        error: () => {
          localStorage.removeItem(this.LAST_SENT_KEY);
        },
      }),
    );
  }

  public readonly remainingTimeBeforeSendingAVerificationEmail = toSignal(
    timer(0, 1000).pipe(
      map(() => {
        const lastSentTime = localStorage.getItem(this.LAST_SENT_KEY);
        if (!lastSentTime) {
          return null;
        }

        const lastSent = new Date(lastSentTime);
        const now = new Date();
        const timeDifference = now.getTime() - lastSent.getTime();

        if (timeDifference >= this.FIVE_MINUTES_IN_MS) {
          return null;
        }

        return new Date(lastSent.getTime() + this.FIVE_MINUTES_IN_MS);
      }),
    ),
  );

  public readonly canSendVerificationEmail = computed(
    () =>
      this.remainingTimeBeforeSendingAVerificationEmail() === null &&
      this.isUserEmailVerificationPluginActive(),
  );

  public clearLastSentTime(): void {
    localStorage.removeItem(this.LAST_SENT_KEY);
  }
}
